import dayjs from 'dayjs';
import { nanoid } from 'nanoid';

import { calcFilters, FilterType, getRequestFiltersByType } from '@webapp/account/src/resources/reports/filter';
import { isFilterDate } from '@webapp/account/src/resources/reports/global-filters';
import type { GlobalFilter, LinkParamFilter } from '@webapp/account/src/resources/reports/global-filters';
import type { Report } from '@webapp/account/src/resources/reports/reports';

import { FILTER_DATE_FORMAT } from '@webapp/common/lib/date';
import { undefDestroy } from '@webapp/common/lib/utils';
import type { Question } from '@webapp/common/resources/entities/question';

let savedUriFilters: any = false;

export const formatDateForFilters = (date: unknown) => {
    const now = dayjs().format(FILTER_DATE_FORMAT);

    // В state/URL иногда прилетает не примитив (FRONT-2ACP/2ACY): dayjs уходит
    // в рекурсию на ToPrimitive и роняет рендер. Не падаем — подставляем now.
    if (!isFilterDate(date)) {
        return now;
    }

    const parsed = dayjs(date);

    return parsed.isValid() ? parsed.format(FILTER_DATE_FORMAT) : now;
};

export const getUriFilters = (): GlobalFilter | null => {
    // used only for pdf render
    // TODO sync with state, do not read directly from location. use state as source of truth

    if (savedUriFilters !== false) {
        return savedUriFilters;
    }

    const uriFilters = new URL(window.location.href).searchParams.get('filters') || null;
    let filters = null;

    try {
        if (uriFilters) {
            filters = decodeURIComponent(uriFilters);
            filters = JSON.parse(filters);
        }
    } catch (e) {
        console.error(e);
    }

    savedUriFilters = filters;

    return filters;
};

// TODO pick globalFilters from state
export const getFilterParams = (globalFilters: GlobalFilter, reports: Array<Report | Question>): AnyObject => {
    const uriFilters = getUriFilters();
    const { dateFrom, dateTo, filters, reportStatus: status, tags: tagsRaw } = uriFilters || globalFilters;
    // Pdf-renderer открывает страницу с фильтрами в URL (?filters=...), и в состоянии
    // страницы их нет. Фильтры по меткам ссылки тоже нужно брать из URL, иначе
    // PDF-отчёт формируется без учёта фильтра по метке (ttgraf/core#762).
    const uriLinkParamsFilters = uriFilters?.linkParamsFilters;
    const linkParamsFilters = Array.isArray(uriLinkParamsFilters)
        ? uriLinkParamsFilters
        : globalFilters.linkParamsFilters;
    const tags = tagsRaw || {};
    const start = formatDateForFilters(dateFrom);
    const end = formatDateForFilters(dateTo);
    const calculatedFilters = filters ? calcFilters(filters, reports) : [];
    const includeFilters = getRequestFiltersByType(calculatedFilters, FilterType.INCLUDE);
    const excludeFilters = getRequestFiltersByType(calculatedFilters, FilterType.EXCLUDE);

    const param_include: Array<LinkParamFilter> = [];
    const param_exclude: Array<LinkParamFilter> = [];

    linkParamsFilters.forEach((f) => {
        if (f.filterType === FilterType.INCLUDE) {
            param_include.push(f);
        } else if (f.filterType === FilterType.EXCLUDE) {
            param_exclude.push(f);
        }
    });

    const tag_include: Array<{ questionId: number; text: string; comment?: boolean }> = [];
    const tag_exclude: Array<{ questionId: number; text: string; comment?: boolean }> = [];

    Object.entries(tags).forEach(([questionId, tagValue]) => {
        tagValue.items.forEach((item) => {
            (item.type === FilterType.INCLUDE ? tag_include : tag_exclude).push({
                questionId: Number(questionId),
                text: item.text,
                comment: item.comment,
            });
        });
    });

    return Object.assign(
        {
            start,
            end,
            status,
        },
        includeFilters?.length ? { include: JSON.stringify(includeFilters) } : {},
        excludeFilters?.length ? { exclude: JSON.stringify(excludeFilters) } : {},
        param_include.length ? { param_include: JSON.stringify(param_include) } : {},
        param_exclude.length ? { param_exclude: JSON.stringify(param_exclude) } : {},
        tag_include.length ? { tag_include: JSON.stringify(tag_include) } : {},
        tag_exclude.length ? { tag_exclude: JSON.stringify(tag_exclude) } : {},
        {
            hidden_params: Array.from(globalFilters.excludedParamIds),
        },
    );
};

// Сквозной идентификатор просмотра отчёта: бэкенд кеширует по нему список
// подходящих респондентов, чтобы не пересчитывать цепочку фильтров на каждый
// мини-запрос get/stat_question и get/list_question.
// Хранится в памяти модуля — при перезагрузке страницы сбрасывается автоматически.
// Кеш — Map, чтобы режим сравнения периодов мог держать ssid сразу для нескольких
// наборов фильтров, а не инвалидировать один слот по кругу (5 периодов убили бы кеш).
const REPORT_SSID_LIMIT = 12; // 5 периодов × запас
const reportSsidCache = new Map<string, string>();

// Ключи, которые не влияют на список респондентов (пагинация, состав запрошенных вопросов)
const ssidVolatileKeys = ['question', 'page', 'limit'];

export const getReportSsid = (surveyId: number, reportParams: AnyObject): string => {
    const filters = { ...reportParams };
    ssidVolatileKeys.forEach((key) => delete filters[key]);

    const fingerprint = JSON.stringify([surveyId, filters]);
    const cached = reportSsidCache.get(fingerprint);

    if (cached) {
        return cached;
    }

    const value = nanoid();
    reportSsidCache.set(fingerprint, value);

    if (reportSsidCache.size > REPORT_SSID_LIMIT) {
        const oldest = reportSsidCache.keys().next().value;
        reportSsidCache.delete(oldest);
    }

    return value;
};

export const patchObject = <T = AnyObject>(obj: T, param: string | AnyObject, value?: PrimitiveValue): T => {
    if (typeof param === 'string') {
        obj[param] = value;
    } else {
        Object.assign(obj, undefDestroy(param));
    }
    return obj;
};
